/** Reviewing a meter export: many rows, one decision.
 *
 * The screen exists to make one thing obvious before anyone clicks: **this is all or nothing**. If
 * any row cannot be read, or names a site that is not in this organisation, nothing is imported.
 *
 * So the errors come first and the button is disabled while any remains. A screen that showed a
 * hundred good rows above a quiet warning would invite someone to import them — and a filed total
 * quietly missing whatever failed is the worst outcome this product has.
 *
 * The **total** is shown prominently for the same reason. It is the number that will move, and
 * seeing it before accepting is the difference between reviewing an import and approving one.
 */

import { useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { useMe } from "../api/auth";
import { useAcceptImport, useDocument, useImportPreview, useReject } from "../api/evidence";
import type { ImportMeterGroup, Imported } from "../api/evidence";
import { DiffRefused, saveColumnMapping } from "../api/queries";
import type { ColumnMap, ColumnsNeedMapping } from "../api/queries";
import { isPeriodClosed } from "../api/refusal";
import { ReadFailed, WriteFailed } from "../components/Loaded";
import { OutsideEngagement } from "../components/OutsideEngagement";
import { Empty, Panel } from "../components/Panel";
import { InfoPopover } from "../components/ui";
import { isEngaged } from "../lib/engagement";
import { shortDate, unitLabel } from "../lib/format";
import styles from "./Evidence.module.css";
import { meterGroupKey } from "./meterGroup";
import { ColumnMapStep } from "./ColumnMapStep";
import { MeterSelect } from "./MeterPicker";
import { PostCloseRetry } from "./PeriodLock";
import { ScreenFrame } from "./ScreenFrame";
import { RequireSession } from "../shell/RequireSession";
import { useScreen } from "./useScreen";

/** A group is a site, a kind of activity and a fuel — what the importer classifies a row by. */
function groupKey(group: ImportMeterGroup): string {
  return meterGroupKey(group);
}

/** **The way out of a file that cannot be imported** (walkthrough C14).
 *
 * A refused import (an unsupported fuel, a site that is not ours) stayed "Ready to read" for ever:
 * the screen said *fix the file and upload it again* and offered no way to retire the one that was
 * refused, so it sat in the library, and Close this period stayed disabled with *"1 document
 * covering this period is still waiting to be reviewed. Verify or reject each one."* The server has
 * always had `POST /evidence/{id}/reject` (`VALIDATED -> REJECTED` is a legal move); the import
 * screen simply never offered it.
 *
 * A reason is required, as everywhere a rejection is made: the person who uploads next needs to
 * know why. Rejecting is terminal, and the sentence says so, because a rejected file cannot be
 * brought back (a corrected one is a new document).
 */
function RejectPanel({
  documentId,
  version,
  mayReject,
}: {
  documentId: string;
  version: number | undefined;
  mayReject: boolean;
}) {
  const navigate = useNavigate();
  const screen = useScreen();
  const reject = useReject();
  const [reason, setReason] = useState("");
  const [done, setDone] = useState<{ already: boolean } | null>(null);

  if (done) {
    return (
      <Panel title="Rejected" column="1 / 13">
        <p className={styles.note} data-testid="import-rejected">
          {done.already
            ? "This file had already been rejected; the reason on record is the first one."
            : "This file is rejected and no longer waits to be reviewed."}{" "}
          Upload a corrected file when you have one.
        </p>
        <button
          className={styles.primary}
          type="button"
          onClick={() => void navigate({ to: "/evidence", search: { method: screen.method } })}
        >
          Back to the evidence library
        </button>
      </Panel>
    );
  }

  return (
    <Panel title="Or reject this file" column="1 / 13">
      <div data-testid="import-reject">
        {mayReject ? (
          <>
            <InfoPopover infoKey="documents.import-reject" />
            <label className={styles.note}>
              Why is this file being rejected?
              <input
                type="text"
                value={reason}
                maxLength={2000}
                data-testid="import-reject-reason"
                onChange={(event) => setReason(event.target.value)}
              />
            </label>
            <button
              className={styles.primary}
              type="button"
              data-testid="import-reject-submit"
              disabled={reject.isPending || reason.trim().length === 0}
              onClick={() =>
                void reject
                  .mutateAsync({ documentId, reason: reason.trim(), expectedVersion: version })
                  .then((answer) => setDone({ already: answer.already_rejected }))
                  .catch(() => undefined)
              }
            >
              {reject.isPending ? "Rejecting…" : "Reject this file"}
            </button>
            <WriteFailed error={reject.error} of="this rejection" onReload={() => reject.reset()} />
          </>
        ) : (
          <p className={styles.note} data-testid="import-reject-who-can">
            An <strong>Admin</strong> or an <strong>Approver</strong> rejects a file. Ask one to
            reject this one if it will not be imported.
          </p>
        )}
      </div>
    </Panel>
  );
}

function ImportScreenBody() {
  const screen = useScreen();
  const navigate = useNavigate();
  const { documentId } = useParams({ from: "/evidence/$documentId/import" });
  /** The mapping a person confirmed for this file's columns (gate 17), or `null` for the file's own
   * names. Held here so the preview **and** the acceptance read one file one way. */
  const [columnMap, setColumnMap] = useState<ColumnMap | null>(null);
  /** The columns the server asked about, kept after a mapping is tried: a mapping the server will
   * not use comes back as a plain sentence, and the step must stay open beside it. */
  const [askedAbout, setAskedAbout] = useState<ColumnsNeedMapping | null>(null);
  /** Whether to remember the mapping once it has produced a preview. After, never before: a mapping
   * the parser refused must not be kept (the round trip's rule). */
  const [remember, setRemember] = useState<{ name: string } | null>(null);
  const [rememberNote, setRememberNote] = useState<string | null>(null);
  const remembered = useRef<{ name: string } | null>(null);
  const {
    data: preview,
    isPending,
    error,
    refetch: retryPreview,
  } = useImportPreview(documentId, true, columnMap);

  useEffect(() => {
    if (!preview || !columnMap || !remember || remembered.current === remember) return;
    // A ref, not state: the effect must run once per confirmed mapping, and setting state here
    // would re-render to record that it had.
    remembered.current = remember;
    const name = remember.name;
    void saveColumnMapping(name, columnMap).then((failed) =>
      setRememberNote(
        failed === null
          ? `Mapping “${name}” remembered. The next file with exactly these columns will be offered it, not given it.`
          : `This mapping was not remembered: ${failed}`,
      ),
    );
  }, [preview, columnMap, remember]);
  const {
    data: document,
    isPending: documentPending,
    error: documentError,
    refetch: retryDocument,
  } = useDocument(documentId);
  const { data: me } = useMe();

  // Accepting is an Approver's act, the same as verifying a statement — ADR-0008 separates
  // preparing from accepting. A Contributor prepares the file and sees exactly what it contains;
  // offering them a button that 403s would be worse than saying whose decision it is.
  const mayAccept = me?.role === "approver" || me?.role === "admin";

  const accept = useAcceptImport();
  const [outcome, setOutcome] = useState<Imported | null>(null);
  const [refusal, setRefusal] = useState<unknown>(null);
  /** The meter a person confirmed for each group of rows, by `groupKey` (ADR-0099).
   *
   * Empty means *not said* and is the only default: nothing here is pre-selected, suggested or
   * filled from the file's own `meter` column, which this screen does not read (SEC-05). A group
   * left out is sent as nothing, and the stream it would have fed keeps showing the month missing. */
  const [meters, setMeters] = useState<Record<string, string>>({});

  async function onAccept(postCloseReason?: string) {
    setRefusal(null);
    try {
      setOutcome(
        await accept.mutateAsync({
          documentId,
          expectedVersion: document?.version,
          postCloseReason,
          columnMap,
          // Only the groups the person answered, and only groups the preview listed: an answer for
          // a group no longer present is dropped here rather than refused by the server.
          meterChoices: (preview?.meter_groups ?? []).flatMap((group) => {
            const meterId = meters[groupKey(group)];
            return meterId
              ? [
                  {
                    facility_id: group.facility_id,
                    category: group.category,
                    fuel_or_carrier: group.fuel_or_carrier,
                    meter_id: meterId,
                  },
                ]
              : [];
          }),
        }),
      );
    } catch (failure) {
      setRefusal(failure);
    }
  }

  if (outcome) {
    return (
      <ScreenFrame screen={screen} title="Imported" crumb="the figures are in the inventory">
        <Panel title="Recorded" column="1 / 8">
          {/* **A retry that already landed is reassurance, not an error** (EXP-35). The ordinary
              way somebody sends this request twice is that the first one timed out at a proxy and
              succeeded — and a screen that showed "0 calculations" without saying why would read
              as an import that did nothing, which sends them to upload the file again. The counts
              are zero because *this* request wrote nothing, which is the point. */}
          {outcome.already_imported ? (
            <p className={styles.note} data-testid="already-imported">
              <strong>This file was already imported.</strong> It produced{" "}
              {outcome.activity_records} activity record
              {outcome.activity_records === 1 ? "" : "s"}, and they are still there — nothing was
              recorded a second time. If your last attempt seemed to fail, it did not.
            </p>
          ) : (
          <p className={styles.note}>
            {outcome.rows} row{outcome.rows === 1 ? "" : "s"} became {outcome.activity_records}{" "}
            activity record{outcome.activity_records === 1 ? "" : "s"},{" "}
            {outcome.calculations} calculation{outcome.calculations === 1 ? "" : "s"} and{" "}
            {outcome.ledger_entries} ledger entr
            {outcome.ledger_entries === 1 ? "y" : "ies"}.
          </p>
          )}
          <button
            className={styles.primary}
            type="button"
            onClick={() => void navigate({ to: "/emissions", search: { method: screen.method } })}
          >
            See them on Emissions
          </button>
        </Panel>
      </ScreenFrame>
    );
  }

  if (isPending) {
    return (
      <ScreenFrame screen={screen} title="Import" crumb="reading">
        {null}
      </ScreenFrame>
    );
  }

  //: **The file is fine and its columns are not ours** - not a dead end (gate 17). The same step the
  //: round trip offers: a person says which column is which, nothing is guessed, and a mapping the
  //: server will not use stays open beside its reason. A refusal that is *not* this one - a file
  //: that cannot be read at all - falls through to the sentence below.
  const asked = error instanceof DiffRefused && error.needsMapping ? error.columns : null;
  const needs = asked ?? (columnMap !== null && error ? askedAbout : null);
  if (needs) {
    return (
      <ScreenFrame screen={screen} title="Import" crumb="map columns">
        <Panel
          title="Map columns"
          sub="this file's columns are not ours: say which is which"
          column="1 / 13"
        >
          <div data-testid="import-column-map">
            <ColumnMapStep
              needs={needs}
              filename={document?.original_filename ?? "this file"}
              busy={false}
              refusal={asked ? null : error instanceof Error ? error.message : null}
              onConfirm={(map, keep) => {
                setAskedAbout(needs);
                setRemember(keep);
                setColumnMap(map);
              }}
              onCancel={() =>
                void navigate({ to: "/evidence", search: { method: screen.method } })
              }
            />
          </div>
        </Panel>
      </ScreenFrame>
    );
  }

  // **A file outside the engagement is not a file that cannot be read** (Q-03, G-Wauditor-04). The
  // preview is refused to an Auditor by role whether or not the file exists, and the document read
  // answers 404 for a file that is not theirs: the same 404 as for one that does not exist. So a
  // document that did not come back, for somebody whose reads a clamp narrows, is said in the one
  // sentence that is true of both - and the reject control, which acts on a file they cannot see, is
  // not offered.
  if ((error || !preview) && isEngaged(me) && !documentPending && document === null) {
    return (
      <ScreenFrame screen={screen} title="Import" crumb="not available">
        <Panel title="Not available" column="1 / 8">
          <OutsideEngagement noun="file" />
        </Panel>
      </ScreenFrame>
    );
  }

  if (error || !preview) {
    return (
      <ScreenFrame screen={screen} title="Import">
        <Panel title="This file cannot be read" column="1 / 8">
          {/* The API's own message. They are written for the person who uploaded the file —
              "export it as CSV UTF-8", "these columns are required" — and replacing them with a
              status code would waste the work of writing them.

              **And a way out** (EXP-29). Most messages here are the file's fault and retrying
              shows the same sentence, which costs nothing; the one that is not is a request that
              timed out at a proxy, and before this the only way on from that was a page reload. */}
          <ReadFailed
            of="The import preview"
            error={error ?? new Error("Nothing to import.")}
            onRetry={() => void retryPreview()}
          />
        </Panel>
        {/* A file that cannot be read is the commonest file nobody will import (C14). */}
        <RejectPanel documentId={documentId} version={document?.version} mayReject={mayAccept} />
      </ScreenFrame>
    );
  }

  const blocked = preview.errors.length > 0 || preview.unmatched_facilities.length > 0;

  return (
    <ScreenFrame
      screen={screen}
      title="Import"
      crumb={`${preview.rows.length} row${preview.rows.length === 1 ? "" : "s"}`}
    >
      {documentError || !document ? (
        <Panel title="Document version" column="1 / 13">
          <div data-testid="import-version-unavailable">
            {documentError ? (
              <ReadFailed
                of="The document version"
                error={documentError}
                onRetry={() => void retryDocument()}
              />
            ) : (
              <p className={styles.note} role="status">
                {documentPending ? "Reading the document version." : "The document was not found."}
              </p>
            )}
            <p className={styles.note}>
              {document
                ? "The last successfully read version is retained. Accepting uses that version for EmitIQ's version check; the latest document read could not be confirmed."
                : "The preview is available, but accepting it now proceeds without a version check. A change to the document since this preview would not be checked by its version."}
            </p>
          </div>
        </Panel>
      ) : null}
      {blocked ? (
        <Panel
          title="Nothing will be imported"
          sub="all or nothing"
          column="1 / 13"
        >
          <p className={styles.note}>
            If any row cannot be read, none is imported — a partial import would leave a filed total
            quietly short. Fix the file and upload it again.
          </p>
          {preview.unmatched_facilities.length > 0 ? (
            <p className={styles.error}>
              These sites are not in this organisation:{" "}
              <strong>{preview.unmatched_facilities.join(", ")}</strong>. Add them, or correct the
              names in the file.
            </p>
          ) : null}
          <ul className={styles.fields}>
            {preview.errors.map((rowError) => (
              <li key={`${rowError.line}-${rowError.column}`} className={`${styles.field_row} ${styles.gap}`}>
                <span className={styles.fieldName}>Line {rowError.line}</span>
                <span className={styles.fieldValue}>{rowError.message}</span>
                <span className={styles.confidence}>{rowError.column ?? ""}</span>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}

      <Panel
        title="What this file contains"
        sub={`${preview.total_quantity} across ${preview.rows.length} rows`}
        column={blocked ? "1 / 13" : "1 / 9"}
      >
        {preview.rows.length === 0 ? (
          <Empty>No rows could be read.</Empty>
        ) : (
          <ul className={styles.fields}>
            {preview.rows.map((row) => (
              <li key={row.line} className={styles.field_row}>
                <span className={styles.fieldName}>{row.facility}</span>
                <span className={styles.fieldValue}>
                  {row.quantity}
                  <span className={styles.unit}> {unitLabel(row.unit)}</span> · {row.category} ·{" "}
                  {shortDate(row.period_start)} – {shortDate(row.period_end)}
                </span>
                <span className={styles.confidence}>{row.line}</span>
              </li>
            ))}
          </ul>
        )}
        {/* What happened while reading the **file**, as opposed to a row: which worksheet was
            read, which cells hold a formula nothing has calculated (ADR-0028), and **which number
            convention the quantities were read in** (UX-05). A workbook has sheets and a CSV does
            not, and a reader who is not told which one was read has to open the file to find out —
            which is the whole reason the preview exists.

            The number-format note is what makes the assumed reading explicit rather than silent:
            `1,234` is 1234 to a British reader and 1.234 to a German one, and the two are a
            thousandfold apart. It is on the panel a person accepts, beside the quantity it
            produced, so a wrong reading is visible before it becomes a figure. */}
        {preview.notes.map((note) => (
          <p key={note} className={styles.note}>
            {note}
          </p>
        ))}
        {columnMap ? (
          <p className={styles.note} data-testid="import-read-with-mapping">
            Read with the column mapping you confirmed. Importing records the file exactly as it is
            shown here.
          </p>
        ) : null}
        {rememberNote ? <p className={styles.note}>{rememberNote}</p> : null}
        {preview.ignored_columns.length > 0 ? (
          <p className={styles.note}>
            Columns not used: {preview.ignored_columns.join(", ")}. If one of those was meant to be
            a quantity or a date, check its spelling.
          </p>
        ) : null}
      </Panel>

      {blocked ? null : (
        <Panel
          title="Accept it"
          sub={mayAccept ? undefined : "Needs an approver"}
          column="9 / 13"
        >
          {mayAccept ? (
            <>
              <p className={styles.note}>
                This will add {preview.rows.length} figure
                {preview.rows.length === 1 ? "" : "s"} to the inventory, each with a receipt naming
                this file and you.
              </p>
              {/* ADR-0099. A question per group that falls on a site with a stream declared for one
                  meter, and nothing at all for a file whose sites have none. The list is the
                  registry's, identical whatever the file's `meter` column says; none is selected
                  until a person selects it. */}
              {preview.meter_groups.length > 0 ? (
                <div data-testid="import-meter-groups">
                  <p className={styles.note}>
                    Some of these rows fall on a site that expects figures from particular meters.
                    Say which meter, and those rows count towards that meter&rsquo;s stream. Leave a
                    group blank and nothing is guessed from the file, so the stream keeps showing
                    the period as missing. Every row in a group takes the one meter you choose, so
                    if a group covers two meters, leave it blank.
                  </p>
                  {preview.meter_groups.map((group) => (
                    <MeterSelect
                      key={groupKey(group)}
                      meters={group.meters}
                      value={meters[groupKey(group)] ?? ""}
                      onChange={(meterId) =>
                        setMeters((current) => ({ ...current, [groupKey(group)]: meterId }))
                      }
                      name={`Which meter were the ${group.category} rows at ${group.facility} for?`}
                      label={`${group.rows} ${group.category.replace(/_/g, " ")} row${group.rows === 1 ? "" : "s"} at ${group.facility}: which meter? (optional)`}
                      note=""
                    />
                  ))}
                </div>
              ) : null}
              <button
                className={styles.primary}
                type="button"
                disabled={accept.isPending}
                onClick={() => void onAccept()}
              >
                {accept.isPending ? "Importing…" : `Import ${preview.rows.length} rows`}
              </button>
            </>
          ) : (
            <p className={styles.note}>
              This file is ready. Figures enter the inventory when an <strong>Approver</strong>
              {" "}accepts them — the person who prepares evidence is not the person who accepts it.
            </p>
          )}
        </Panel>
      )}

      <RejectPanel documentId={documentId} version={document?.version} mayReject={mayAccept} />

      {/* **A retry of this request is answered rather than repeated** (EXP-35): `POST
          /evidence/{id}/import` reports `already_imported` before the state machine, with the
          counts the first attempt produced. So this is the one write in the product where the
          unknown-outcome sentence can honestly end "sending it again is safe", and `retrySafe`
          says so rather than `WriteFailed` assuming it. */}
      {/* A row that reaches a period closed after this file was uploaded (ADR-0094). All or nothing,
          so the refusal names every closed period the file touches and an Admin's retry sends the
          same acceptance with a reason. */}
      <PostCloseRetry
        error={refusal}
        pending={accept.isPending}
        onRecord={(reason) => void onAccept(reason)}
      />
      <WriteFailed
        error={isPeriodClosed(refusal) ? null : refusal}
        of="this import"
        retrySafe
        onReload={() => void retryPreview()}
      />
    </ScreenFrame>
  );
}

export function ImportScreen() {
  return (
    <RequireSession>
      <ImportScreenBody />
    </RequireSession>
  );
}
